import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GetReportApi } from "../../ApiRequests";
import {
  mergeReportPayloads,
  splitReportDateChunks,
} from "../../utils/reportChunkFetch";

export const fetchReport = createAsyncThunk(
  "fetch/Daily-Report",
  async (data, { signal, rejectWithValue }) => {
    try {
      // Wide ranges (e.g. This Year) → load month-sized chunks so each
      // backend call finishes before Vercel/proxy timeouts.
      const chunks = splitReportDateChunks(data?.startDate, data?.endDate);
      const parts = [];

      for (const chunk of chunks) {
        if (signal?.aborted) {
          return rejectWithValue("aborted");
        }
        const response = await GetReportApi(
          {
            ...data,
            startDate: chunk.startDate,
            endDate: chunk.endDate,
          },
          { signal }
        );
        const payload = response?.data?.data?.payload;
        if (payload && typeof payload === "object" && !Array.isArray(payload)) {
          parts.push(payload);
        } else {
          console.warn("Daily report: unexpected response shape", response?.data);
          return rejectWithValue("Unexpected report response");
        }
      }

      const merged = mergeReportPayloads(parts, {
        startDate: data?.startDate || null,
        endDate: data?.endDate || null,
        branchId: data?.branchId || null,
      });
      if (!merged) {
        return rejectWithValue("Unexpected report response");
      }
      return merged;
    } catch (error) {
      if (
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        signal?.aborted
      ) {
        return rejectWithValue("aborted");
      }
      console.error("Daily report fetch failed:", error);
      throw error;
    }
  }
);

const ReportSlice = createSlice({
  name: "Daily-Report",
  initialState: {
    loading: false,
    refreshing: false,
    data: null,
    isError: false,
    activeRequestId: null,
  },
  reducers: {
    /** Clear in-flight flags so /report doesn't inherit loading from other pages. */
    resetReportStatus: (state) => {
      state.loading = false;
      state.refreshing = false;
      state.isError = false;
      // keep activeRequestId so an in-flight DailyReport fulfill can still land
    },
    /** Safe page_no patch for populated customer on report invoices (Immer draft). */
    updateCustomerPageNoInReport: (state, action) => {
      const customerId = action.payload?.customerId;
      const pageNo = action.payload?.page_no;
      if (!customerId || !state.data || typeof state.data !== "object") return;

      const patchList = (list) => {
        if (!Array.isArray(list)) return;
        for (const inv of list) {
          const cust = inv?.customerId;
          if (!cust || typeof cust !== "object") continue;
          if (String(cust._id) !== String(customerId)) continue;
          cust.page_no = pageNo;
        }
      };

      patchList(state.data.transactions);
      patchList(state.data.guest_transactions);
      patchList(state.data.returns);
      patchList(state.data.guest_returns);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchReport.pending, (state, action) => {
      state.activeRequestId = action.meta.requestId;
      // Drop stale payload when the requested range changes (avoids showing
      // today's data while "This Year" / new range is loading or fails).
      const nextStart = action.meta?.arg?.startDate;
      const nextEnd = action.meta?.arg?.endDate;
      const prevStart = state.data?._query?.startDate;
      const prevEnd = state.data?._query?.endDate;
      if (
        state.data &&
        (String(prevStart || "") !== String(nextStart || "") ||
          String(prevEnd || "") !== String(nextEnd || ""))
      ) {
        state.data = null;
      }
      // First load → full spinner; later date changes keep UI mounted
      if (!state.data) {
        state.loading = true;
      } else {
        state.refreshing = true;
      }
      state.isError = false;
    });
    builder.addCase(fetchReport.fulfilled, (state, action) => {
      if (state.activeRequestId !== action.meta.requestId) return;
      state.loading = false;
      state.refreshing = false;
      state.data = action.payload;
      state.isError = false;
    });
    builder.addCase(fetchReport.rejected, (state, action) => {
      // Always clear flags for this request (including abort)
      if (state.activeRequestId === action.meta.requestId) {
        state.loading = false;
        state.refreshing = false;
        state.activeRequestId = null;
      }
      // Aborted / superseded requests are not user-facing errors
      if (
        action.meta.aborted ||
        action.payload === "aborted" ||
        action.error?.name === "AbortError" ||
        action.error?.message === "Aborted"
      ) {
        return;
      }
      if (state.activeRequestId && state.activeRequestId !== action.meta.requestId) {
        return;
      }
      state.isError = true;
    });
  },
});

export const { resetReportStatus, updateCustomerPageNoInReport } =
  ReportSlice.actions;
export default ReportSlice.reducer;
